import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import axios from 'axios';
import { toast } from 'sonner';
import { API, getAuthHeaders, removeAuthToken } from '@/App';
import {
  Link2, LogOut, BarChart3, MapPin, Globe, Copy, Users, Clock, Edit, X, ExternalLink, Smartphone, MessageCircle, Chrome
} from 'lucide-react';
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import DateRangePicker from '@/components/DateRangePicker';

const UserDashboard = () => {
  const navigate = useNavigate();
  const [links, setLinks] = useState([]);
  const [selectedLink, setSelectedLink] = useState(null);
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');
  const [selectedDays, setSelectedDays] = useState(7);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingLink, setEditingLink] = useState(null);
  const [editForm, setEditForm] = useState({ destination_url: '', title: '' });
  const [showWhatsAppModal, setShowWhatsAppModal] = useState(false);
  const [showStatsModal, setShowStatsModal] = useState(false);
  const [statsModalLink, setStatsModalLink] = useState(null);
  const [statsModalData, setStatsModalData] = useState(null);

  const fetchLinks = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/user/links`, { headers: getAuthHeaders() });
      setLinks(res.data);
      if (res.data.length > 0) {
        setSelectedLink(res.data[0]);
        fetchAnalytics(res.data[0].id, 7);
      }
      setLoading(false);
    } catch (error) {
      toast.error('Veriler yüklenemedi');
      if (error.response?.status === 401) {
        removeAuthToken();
        navigate('/login');
      }
    }
  }, [navigate]);

  const fetchAnalytics = useCallback(async (linkId, days) => {
    try {
      const res = await axios.get(`${API}/analytics/link/${linkId}?days=${days}`, { headers: getAuthHeaders() });
      setAnalytics(res.data);
    } catch (error) {
      console.error('Analitik yüklenemedi');
    }
  }, []);

  // Kullanıcının genel istatistiklerini getir (tüm linkleri için)
  const fetchUserAnalytics = useCallback(async (days) => {
    try {
      const res = await axios.get(`${API}/user/analytics?days=${days}`, { headers: getAuthHeaders() });
      setAnalytics(res.data);
    } catch (error) {
      console.error('Genel analitik yüklenemedi');
    }
  }, []);

  useEffect(() => {
    fetchLinks();
  }, [fetchLinks]);

  useEffect(() => {
    if (selectedLink) {
      fetchAnalytics(selectedLink.id, selectedDays);
    } else {
      // Hiçbir link seçili değilse, kullanıcının genel istatistiklerini göster
      fetchUserAnalytics(selectedDays);
    }
  }, [selectedDays, selectedLink, fetchAnalytics, fetchUserAnalytics]);

  const handleLogout = () => {
    removeAuthToken();
    navigate('/login');
  };

  const handleLinkSelect = (link) => {
    setSelectedLink(link);
    fetchAnalytics(link.id, selectedDays);
  };

  const copyLink = (shortCode) => {
    navigator.clipboard.writeText(`${window.location.origin}/${shortCode}`);
    toast.success('Link kopyalandı!');
  };

  const openEditModal = (link) => {
    setEditingLink(link);
    setEditForm({ destination_url: link.destination_url, title: link.title || '' });
    setShowEditModal(true);
  };

  const openStatsModal = async (link) => {
    setStatsModalLink(link);
    setShowStatsModal(true);
    try {
      const res = await axios.get(`${API}/analytics/link/${link.id}?days=${selectedDays}`, { headers: getAuthHeaders() });
      setStatsModalData(res.data);
    } catch (error) {
      toast.error('İstatistikler yüklenemedi');
    }
  };

  const handleUpdateLink = async () => {
    try {
      await axios.put(`${API}/user/links/${editingLink.id}`, editForm, { headers: getAuthHeaders() });
      toast.success('Link güncellendi!');
      setShowEditModal(false);
      fetchLinks();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Güncelleme başarısız');
    }
  };

  // URL kısaltma
  const truncateUrl = (url, maxLen = 30) => {
    if (!url) return '-';
    return url.length <= maxLen ? url : url.substring(0, maxLen) + '...';
  };

  // Ülke adından ülke kodunu al
  const getCountryCode = (countryName) => {
    if (!countryName) return null;
    const countries = {
      'Turkey': 'TR', 'Türkiye': 'TR',
      'Germany': 'DE', 'Almanya': 'DE',
      'United Kingdom': 'GB', 'İngiltere': 'GB',
      'United States': 'US', 'Amerika': 'US',
      'France': 'FR', 'Fransa': 'FR',
      'Netherlands': 'NL', 'Hollanda': 'NL',
      'Denmark': 'DK', 'Danimarka': 'DK',
      'Libya': 'LY',
      'Russia': 'RU', 'Rusya': 'RU',
      'Italy': 'IT', 'İtalya': 'IT',
      'Spain': 'ES', 'İspanya': 'ES',
      'Belgium': 'BE', 'Belçika': 'BE',
      'Austria': 'AT', 'Avusturya': 'AT',
      'Switzerland': 'CH', 'İsviçre': 'CH',
      'Sweden': 'SE', 'İsveç': 'SE',
      'Norway': 'NO', 'Norveç': 'NO',
      'Finland': 'FI', 'Finlandiya': 'FI',
      'Poland': 'PL', 'Polonya': 'PL',
      'Greece': 'GR', 'Yunanistan': 'GR',
      'Portugal': 'PT', 'Portekiz': 'PT',
      'Ireland': 'IE', 'İrlanda': 'IE',
      'Czech Republic': 'CZ', 'Çekya': 'CZ',
      'Hungary': 'HU', 'Macaristan': 'HU',
      'Romania': 'RO', 'Romanya': 'RO',
      'Bulgaria': 'BG', 'Bulgaristan': 'BG',
      'Ukraine': 'UA', 'Ukrayna': 'UA',
      'Canada': 'CA', 'Kanada': 'CA',
      'Australia': 'AU', 'Avustralya': 'AU',
      'Japan': 'JP', 'Japonya': 'JP',
      'China': 'CN', 'Çin': 'CN',
      'India': 'IN', 'Hindistan': 'IN',
      'Brazil': 'BR', 'Brezilya': 'BR',
      'Mexico': 'MX', 'Meksika': 'MX',
      'Argentina': 'AR', 'Arjantin': 'AR',
      'South Korea': 'KR', 'Güney Kore': 'KR',
      'Saudi Arabia': 'SA', 'Suudi Arabistan': 'SA',
      'United Arab Emirates': 'AE', 'BAE': 'AE',
      'Egypt': 'EG', 'Mısır': 'EG',
      'Morocco': 'MA', 'Fas': 'MA',
      'South Africa': 'ZA', 'Güney Afrika': 'ZA',
      'Israel': 'IL', 'İsrail': 'IL',
      'Singapore': 'SG', 'Singapur': 'SG',
      'Malaysia': 'MY', 'Malezya': 'MY',
      'Thailand': 'TH', 'Tayland': 'TH',
      'Vietnam': 'VN',
      'Indonesia': 'ID', 'Endonezya': 'ID',
      'Philippines': 'PH', 'Filipinler': 'PH',
      'Pakistan': 'PK',
      'Bangladesh': 'BD', 'Bangladeş': 'BD',
      'Iran': 'IR', 'İran': 'IR',
      'Iraq': 'IQ', 'Irak': 'IQ',
      'Syria': 'SY', 'Suriye': 'SY',
      'Azerbaijan': 'AZ', 'Azerbaycan': 'AZ',
      'Georgia': 'GE', 'Gürcistan': 'GE',
      'Kazakhstan': 'KZ', 'Kazakistan': 'KZ',
      'Uzbekistan': 'UZ', 'Özbekistan': 'UZ'
    };
    return countries[countryName] || null;
  };

  // Referrer verileri
  const referrerData = useMemo(() => {
    if (!analytics?.referrers) return [];
    return Object.entries(analytics.referrers)
      .sort((a, b) => b[1] - a[1])
      .slice(0, 5)
      .map(([name, value], idx) => ({
        name: name.length > 15 ? name.substring(0, 15) + '...' : name,
        fullName: name,
        value,
        color: ['#FFD700', '#FF8C00', '#FF4500', '#FF1493', '#9370DB'][idx]
      }));
  }, [analytics?.referrers]);

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0a0a0a] flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-purple-500"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0a0a0a]" data-testid="user-dashboard">
      {/* Navbar */}
      <nav className="border-b border-white/10 bg-black/50 backdrop-blur-md sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-3 py-3 flex justify-between items-center">
          <div className="flex items-center gap-2">
            <img src="/sl-logo.png" alt="SnapLink" className="w-8 h-8 rounded-lg" />
            <h1 className="text-base font-bold text-white">SnapLink</h1>
          </div>
          <button onClick={handleLogout} className="flex items-center gap-1 text-gray-400 hover:text-white text-sm">
            <LogOut className="w-4 h-4" />
            <span className="hidden sm:inline">Çıkış</span>
          </button>
        </div>
      </nav>

      <div className="max-w-7xl mx-auto px-3 py-4">
        {/* Linklerim */}
        <div className="mb-6">
          <h2 className="text-lg font-bold text-white mb-3">Linklerim</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {links.map((link) => (
              <div
                key={link.id}
                onClick={() => handleLinkSelect(link)}
                className={`bg-[#111] border rounded-lg p-3 cursor-pointer transition-all ${
                  selectedLink?.id === link.id ? 'border-purple-500' : 'border-white/10 hover:border-white/20'
                }`}
              >
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-purple-500 font-mono text-sm font-bold">/{link.short_code}</span>
                    <span className="text-[10px] px-2 py-0.5 rounded bg-green-500/20 text-green-500">Aktif</span>
                  </div>
                  <button
                    onClick={(e) => { e.stopPropagation(); copyLink(link.short_code); }}
                    className="p-1.5 bg-white/10 hover:bg-white/20 rounded-lg transition-colors"
                    title="Kopyala"
                  >
                    <Copy className="w-4 h-4 text-gray-400" />
                  </button>
                </div>
                {link.title && <div className="text-xs text-gray-400 mb-1">{link.title}</div>}
                <div className="text-[10px] text-gray-500 truncate mb-3">{truncateUrl(link.destination_url, 35)}</div>
                
                {/* Aksiyon Butonları - Görünür */}
                <div className="flex items-center gap-2 flex-wrap">
                  <button
                    onClick={(e) => { e.stopPropagation(); window.open(`${window.location.origin}/${link.short_code}`, '_blank'); }}
                    className="flex items-center gap-1 px-3 py-1.5 bg-green-500/20 hover:bg-green-500/30 border border-green-500/50 rounded-lg text-green-500 text-xs font-medium transition-all"
                    title="Test Et"
                  >
                    <ExternalLink className="w-3.5 h-3.5" />
                    Test Et
                  </button>
                  <button
                    onClick={(e) => { e.stopPropagation(); openStatsModal(link); }}
                    className="flex items-center gap-1 px-3 py-1.5 bg-blue-500/20 hover:bg-blue-500/30 border border-blue-500/50 rounded-lg text-blue-500 text-xs font-medium transition-all"
                    title="İstatistikler"
                  >
                    <BarChart3 className="w-3.5 h-3.5" />
                    İstatistikler
                  </button>
                  <button
                    onClick={(e) => { e.stopPropagation(); openEditModal(link); }}
                    className="flex items-center gap-1 px-3 py-1.5 bg-purple-500/20 hover:bg-purple-500/30 border border-purple-500/50 rounded-lg text-purple-500 text-xs font-medium transition-all"
                    title="Düzenle"
                  >
                    <Edit className="w-3.5 h-3.5" />
                    Düzenle
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Analytics Tabs */}
        {selectedLink && analytics && (
          <>
            {/* Tabs - Ayrı satırda DateRangePicker */}
            <div className="mb-4">
              <div className="flex gap-2 mb-2 overflow-x-auto pb-2 scrollbar-hide">
                {[
                  { id: 'overview', icon: BarChart3, label: 'Özet' },
                  { id: 'countries', icon: Globe, label: 'Ülkeler' },
                  { id: 'cities', icon: MapPin, label: 'Şehirler' },
                  { id: 'devices', icon: Smartphone, label: 'Cihazlar' }
                ].map((tab) => (
                  <button
                    key={tab.id}
                    onClick={() => setActiveTab(tab.id)}
                    className={`flex items-center gap-1 px-3 py-2 rounded-lg whitespace-nowrap text-xs transition-all ${
                      activeTab === tab.id ? 'btn-purple' : 'bg-white/5 text-gray-400 hover:bg-white/10'
                    }`}
                  >
                    <tab.icon className="w-3 h-3" />
                    {tab.label}
                  </button>
                ))}
              </div>
              <DateRangePicker onDateChange={(days) => setSelectedDays(days)} initialDays={selectedDays} />
            </div>

            {/* Stats Cards */}
            <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-4">
              {/* Normal kartlar */}
              {[
                { label: 'Toplam Tıklama', value: analytics.total_clicks, icon: BarChart3, color: 'text-purple-500' },
                { label: 'Benzersiz Tıklama', value: analytics.unique_clicks, icon: Users, color: 'text-purple-500' }
              ].map((stat, idx) => (
                <div key={idx} className="stat-led-card-purple p-3">
                  <div className="flex items-center justify-between mb-2">
                    <div className="text-xs text-gray-400">{stat.label}</div>
                    <stat.icon className={`w-4 h-4 ${stat.color}`} />
                  </div>
                  <div className="text-xl font-bold text-white">{stat.value}</div>
                </div>
              ))}
              
              {/* WhatsApp Kartı - Tıklanabilir */}
              <div 
                onClick={() => setShowWhatsAppModal(true)}
                className="stat-led-card-purple p-3 bg-green-500/10 border-green-500/50 cursor-pointer hover:bg-green-500/20 transition-all"
              >
                <div className="flex items-center justify-between mb-2">
                  <div className="text-xs text-gray-400">WhatsApp</div>
                  <img src="/whatsapp-logo.png" alt="WhatsApp" className="w-5 h-5 rounded" />
                </div>
                <div className="text-xl font-bold text-green-400">{analytics.today_whatsapp || 0}</div>
                <div className="text-[10px] text-gray-500">Bugün</div>
              </div>
              
              {/* Diğer kartlar */}
              {[
                { label: 'Farklı Ülke', value: Object.keys(analytics.countries || {}).length, icon: Globe, color: 'text-purple-500' },
                { label: 'Farklı Şehir', value: Object.keys(analytics.cities || {}).length, icon: MapPin, color: 'text-purple-500' }
              ].map((stat, idx) => (
                <div key={idx + 3} className="stat-led-card-purple p-3">
                  <div className="flex items-center justify-between mb-2">
                    <div className="text-xs text-gray-400">{stat.label}</div>
                    <stat.icon className={`w-4 h-4 ${stat.color}`} />
                  </div>
                  <div className="text-xl font-bold text-white">{stat.value}</div>
                </div>
              ))}
            </div>

            {/* Overview Tab */}
            {activeTab === 'overview' && (
              <div className="space-y-4">
                {/* Chart - TURUNCU TEMA */}
                <div className="bg-[#0a0a0a] rounded-xl p-4 md:p-6">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-base md:text-lg font-bold text-white">Günlük Tıklamalar</h3>
                    {/* Legend */}
                    <div className="flex items-center gap-3 text-xs">
                      <div className="flex items-center gap-1">
                        <div className="w-2 h-2 rounded-full bg-purple-500"></div>
                        <span className="text-gray-400">Tıklama</span>
                      </div>
                      <div className="flex items-center gap-1">
                        <div className="w-2 h-2 rounded-full bg-green-500"></div>
                        <span className="text-gray-400">WhatsApp</span>
                      </div>
                    </div>
                  </div>
                  <div className="h-[280px] sm:h-[300px] md:h-[320px] lg:h-[350px]">
                    <ResponsiveContainer width="100%" height="100%">
                      <AreaChart data={analytics.daily_clicks || []} margin={{ top: 10, right: 10, left: -10, bottom: 0 }}>
                        <defs>
                          <linearGradient id="userColorClicks" x1="0" y1="0" x2="0" y2="1">
                            <stop offset="5%" stopColor="#8b5cf6" stopOpacity={0.9}/>
                            <stop offset="50%" stopColor="#ea580c" stopOpacity={0.4}/>
                            <stop offset="95%" stopColor="#c2410c" stopOpacity={0.1}/>
                          </linearGradient>
                          <linearGradient id="userColorWhatsApp" x1="0" y1="0" x2="0" y2="1">
                            <stop offset="5%" stopColor="#22c55e" stopOpacity={0.8}/>
                            <stop offset="95%" stopColor="#22c55e" stopOpacity={0.1}/>
                          </linearGradient>
                        </defs>
                        <CartesianGrid strokeDasharray="3 3" stroke="#333" vertical={false} />
                        <XAxis 
                          dataKey="date" 
                          stroke="#888" 
                          style={{ fontSize: '10px' }}
                          tickFormatter={(value) => {
                            const date = new Date(value);
                            return `${date.getDate()}/${date.getMonth() + 1}`;
                          }}
                          tick={{ fill: '#888' }}
                          interval={0}
                          angle={-45}
                          textAnchor="end"
                          height={50}
                        />
                        <YAxis stroke="#888" style={{ fontSize: '10px' }} tick={{ fill: '#888' }} />
                        <Tooltip 
                          contentStyle={{ 
                            background: '#1a1a1a', 
                            border: '1px solid #8b5cf6', 
                            borderRadius: '8px', 
                            fontSize: '13px',
                            color: '#fff'
                          }}
                          formatter={(value, name) => [value, name === 'clicks' ? 'Tıklama' : 'WhatsApp']}
                          labelFormatter={(label) => `Tarih: ${label}`}
                        />
                        <Area 
                          type="monotone" 
                          dataKey="clicks" 
                          stroke="#8b5cf6" 
                          strokeWidth={3}
                          fillOpacity={1} 
                          fill="url(#userColorClicks)" 
                          name="clicks"
                          dot={{ r: 5, fill: '#8b5cf6', stroke: '#fff', strokeWidth: 2 }}
                          activeDot={{ r: 7, fill: '#8b5cf6', stroke: '#fff', strokeWidth: 2 }}
                        />
                        <Area 
                          type="monotone" 
                          dataKey="whatsapp" 
                          stroke="#22c55e" 
                          strokeWidth={2}
                          fillOpacity={1} 
                          fill="url(#userColorWhatsApp)" 
                          name="whatsapp"
                          dot={{ r: 4, fill: '#22c55e', stroke: '#fff', strokeWidth: 2 }}
                          activeDot={{ r: 6, fill: '#22c55e', stroke: '#fff', strokeWidth: 2 }}
                        />
                      </AreaChart>
                    </ResponsiveContainer>
                  </div>
                </div>

                {/* Trafik Kaynakları */}
                <div className="bg-[#0a0a0a] rounded-xl p-4">
                  <h3 className="text-sm font-bold text-white mb-3">Trafik Kaynakları</h3>
                  <div className="space-y-3">
                    {referrerData.map((item, idx) => (
                      <div key={idx} className="space-y-1">
                        <div className="flex justify-between items-center text-xs">
                          <span className="bg-black text-white px-2 py-1 rounded border border-purple-500 truncate max-w-[150px]" title={item.fullName}>
                            {item.name}
                          </span>
                          <span className="text-gray-400 ml-2">{item.value}</span>
                        </div>
                        <div className="h-4 bg-white/5 rounded overflow-hidden">
                          <div
                            className="h-full flex items-center px-2 text-white text-[10px] font-bold"
                            style={{
                              width: `${Math.max((item.value / (referrerData[0]?.value || 1)) * 100, 5)}%`,
                              background: item.color
                            }}
                          >
                            {Math.round((item.value / (referrerData[0]?.value || 1)) * 100)}%
                          </div>
                        </div>
                      </div>
                    ))}
                    {referrerData.length === 0 && <p className="text-gray-400 text-xs">Henüz veri yok</p>}
                  </div>
                </div>

                {/* Son Tıklamalar */}
                <div className="bg-[#0a0a0a] rounded-xl p-4">
                  <h3 className="text-sm font-bold text-white mb-3 flex items-center gap-2">
                    <Clock className="w-4 h-4 text-purple-500" />
                    Son Tıklamalar
                  </h3>
                  <div className="overflow-x-auto -mx-4 px-4 max-h-96 overflow-y-auto">
                    <table className="w-full text-xs min-w-[550px]">
                      <thead className="border-b border-white/10 sticky top-0 bg-[#0a0a0a]">
                        <tr className="text-gray-400">
                          <th className="text-left p-2 whitespace-nowrap">Ülke</th>
                          <th className="text-left p-2 whitespace-nowrap">Şehir</th>
                          <th className="text-left p-2 whitespace-nowrap">Kaynak</th>
                          <th className="text-right p-2 whitespace-nowrap min-w-[120px]">Tarih / Saat</th>
                        </tr>
                      </thead>
                      <tbody>
                        {analytics.recent_clicks?.map((click, idx) => {
                          const countryCode = getCountryCode(click.country);
                          return (
                            <tr key={idx} className="border-b border-white/5 text-gray-300">
                              <td className="p-2 whitespace-nowrap">
                                <span className="flex items-center gap-2">
                                  {countryCode && (
                                    <img 
                                      src={`https://flagcdn.com/24x18/${countryCode.toLowerCase()}.png`}
                                      alt={click.country}
                                      className="w-5 h-3.5 object-cover rounded-sm"
                                      onError={(e) => { e.target.style.display = 'none'; }}
                                    />
                                  )}
                                  <span>{click.country || '-'}</span>
                                </span>
                              </td>
                              <td className="p-2 whitespace-nowrap">{click.city || '-'}</td>
                              <td className="p-2 whitespace-nowrap">
                                <span className="bg-black text-white px-1 py-0.5 rounded text-[10px] border border-purple-500">
                                  {click.referrer ? (click.referrer.includes('instagram') ? 'Instagram' : 
                                   click.referrer.includes('facebook') ? 'Facebook' : 
                                   click.referrer.includes('twitter') || click.referrer.includes('t.co') ? 'Twitter' :
                                   click.referrer.includes('tiktok') ? 'TikTok' :
                                   click.referrer.includes('youtube') ? 'YouTube' : 'Direct') : 'Direct'}
                                </span>
                              </td>
                              <td className="p-2 text-right whitespace-nowrap min-w-[120px]">
                                <div className="flex flex-col items-end">
                                  <span className="text-purple-500 font-bold text-xs">
                                    {click.timestamp ? new Date(click.timestamp).toLocaleString('tr-TR', { 
                                      hour: '2-digit', minute: '2-digit' 
                                    }) : '-'}
                                  </span>
                                  <span className="text-gray-400 text-[10px]">
                                    {click.timestamp ? new Date(click.timestamp).toLocaleString('tr-TR', { 
                                      day: '2-digit', month: '2-digit', year: 'numeric'
                                    }) : ''}
                                  </span>
                                </div>
                              </td>
                            </tr>
                          );
                        }) || (
                          <tr><td colSpan="4" className="text-center py-4 text-gray-400">Henüz tıklama yok</td></tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            )}

            {/* Countries Tab */}
            {activeTab === 'countries' && (
              <div className="bg-[#0a0a0a] rounded-xl p-4">
                <h3 className="text-sm font-bold text-white mb-3">Tüm Ülkeler</h3>
                <div className="space-y-2 max-h-96 overflow-y-auto">
                  {Object.entries(analytics.countries || {}).map(([country, count]) => {
                    const countryCode = getCountryCode(country);
                    return (
                      <div key={country} className="flex justify-between items-center p-2 bg-white/5 rounded text-sm">
                        <span className="text-white flex items-center gap-2">
                          {countryCode && (
                            <img 
                              src={`https://flagcdn.com/24x18/${countryCode.toLowerCase()}.png`}
                              alt={country}
                              className="w-6 h-4 object-cover rounded-sm"
                              onError={(e) => { e.target.style.display = 'none'; }}
                            />
                          )}
                          {country}
                        </span>
                        <span className="text-purple-500 font-bold">{count}</span>
                      </div>
                    );
                  })}
                  {Object.keys(analytics.countries || {}).length === 0 && (
                    <p className="text-gray-400 text-sm">Henüz ülke verisi yok</p>
                  )}
                </div>
              </div>
            )}

            {/* Cities Tab */}
            {activeTab === 'cities' && (
              <div className="bg-[#0a0a0a] rounded-xl p-4">
                <h3 className="text-sm font-bold text-white mb-3">Tüm Şehirler</h3>
                <div className="space-y-2 max-h-96 overflow-y-auto">
                  {Object.entries(analytics.cities || {}).map(([city, count]) => (
                    <div key={city} className="flex justify-between items-center p-2 bg-white/5 rounded text-sm">
                      <span className="text-white">{city}</span>
                      <span className="text-purple-500 font-bold">{count}</span>
                    </div>
                  ))}
                  {Object.keys(analytics.cities || {}).length === 0 && (
                    <p className="text-gray-400 text-sm">Henüz şehir verisi yok</p>
                  )}
                </div>
              </div>
            )}

            {/* Devices Tab */}
            {activeTab === 'devices' && (
              <div className="space-y-4">
                {/* Cihaz Tipi */}
                <div className="bg-[#0a0a0a] rounded-xl p-4">
                  <h3 className="text-sm font-bold text-white mb-3 flex items-center gap-2">
                    <Smartphone className="w-4 h-4 text-purple-500" />
                    Cihaz Tipi
                  </h3>
                  <div className="space-y-3">
                    {(() => {
                      // Sadece Mobile, Desktop, Tablet değerlerini filtrele
                      const validPlatforms = ['Mobile', 'Desktop', 'Tablet'];
                      const filteredPlatforms = Object.entries(analytics.platforms || {})
                        .filter(([platform]) => validPlatforms.includes(platform));
                      const total = filteredPlatforms.reduce((sum, [, count]) => sum + count, 0);
                      
                      return filteredPlatforms.length > 0 ? filteredPlatforms.map(([platform, count]) => {
                        const percentage = total > 0 ? Math.round((count / total) * 100) : 0;
                        const getDeviceIcon = (p) => {
                          if (p === 'Mobile') return '📱';
                          if (p === 'Tablet') return '📱';
                          if (p === 'Desktop') return '💻';
                          return '🖥️';
                        };
                        const getDeviceColor = (p) => {
                          if (p === 'Mobile') return '#22c55e';
                          if (p === 'Tablet') return '#3b82f6';
                          if (p === 'Desktop') return '#8b5cf6';
                          return '#8b5cf6';
                        };
                        return (
                          <div key={platform} className="space-y-1">
                            <div className="flex justify-between items-center text-sm">
                              <span className="text-white flex items-center gap-2">
                                <span className="text-lg">{getDeviceIcon(platform)}</span>
                                {platform === 'Mobile' ? 'Mobil' : platform === 'Desktop' ? 'Masaüstü' : 'Tablet'}
                              </span>
                              <span className="text-gray-400">{count} ({percentage}%)</span>
                            </div>
                            <div className="h-4 bg-white/5 rounded overflow-hidden">
                              <div
                                className="h-full rounded flex items-center justify-end pr-2"
                                style={{
                                  width: `${Math.max(percentage, 5)}%`,
                                  backgroundColor: getDeviceColor(platform)
                                }}
                              >
                                <span className="text-white text-[10px] font-bold">{percentage}%</span>
                              </div>
                            </div>
                          </div>
                        );
                      }) : <p className="text-gray-400 text-sm">Henüz cihaz verisi yok</p>;
                    })()}
                  </div>
                </div>

                {/* Tarayıcılar */}
                <div className="bg-[#0a0a0a] rounded-xl p-4">
                  <h3 className="text-sm font-bold text-white mb-3 flex items-center gap-2">
                    <Globe className="w-4 h-4 text-purple-500" />
                    Tarayıcılar
                  </h3>
                  <div className="space-y-2 max-h-64 overflow-y-auto">
                    {Object.entries(analytics.browsers || {}).map(([browser, count]) => {
                      const getBrowserIcon = (b) => {
                        if (b.includes('Chrome')) return '🌐';
                        if (b.includes('Safari')) return '🧭';
                        if (b.includes('Firefox')) return '🦊';
                        if (b.includes('Edge')) return '📘';
                        if (b.includes('Opera')) return '🔴';
                        return '🌍';
                      };
                      return (
                        <div key={browser} className="flex justify-between items-center p-2 bg-white/5 rounded text-sm">
                          <span className="text-white truncate flex items-center gap-2">
                            <span>{getBrowserIcon(browser)}</span>
                            {browser}
                          </span>
                          <span className="text-purple-500 font-bold ml-2">{count}</span>
                        </div>
                      );
                    })}
                    {Object.keys(analytics.browsers || {}).length === 0 && (
                      <p className="text-gray-400 text-sm">Henüz tarayıcı verisi yok</p>
                    )}
                  </div>
                </div>
              </div>
            )}
          </>
        )}

        {links.length === 0 && (
          <div className="text-center py-12">
            <Link2 className="w-16 h-16 text-gray-600 mx-auto mb-4" />
            <p className="text-gray-400">Henüz linkiniz yok</p>
            <p className="text-gray-500 text-sm mt-2">Admin tarafından size link atandığında burada görünecek</p>
          </div>
        )}
      </div>

      {/* Edit Modal */}
      <AnimatePresence>
        {showEditModal && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
            <motion.div initial={{ scale: 0.9 }} animate={{ scale: 1 }} exit={{ scale: 0.9 }} className="bg-[#111] border border-white/10 rounded-xl p-4 w-full max-w-md">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-bold text-white">Link Düzenle</h3>
                <button onClick={() => setShowEditModal(false)} className="text-gray-400 hover:text-white"><X className="w-5 h-5" /></button>
              </div>
              <div className="space-y-3">
                <div>
                  <label className="text-xs text-gray-400 mb-1 block">Kısa Kod</label>
                  <div className="text-purple-500 font-mono bg-white/5 px-3 py-2 rounded-lg">/{editingLink?.short_code}</div>
                </div>
                <div>
                  <label className="text-xs text-gray-400 mb-1 block">Hedef URL</label>
                  <input
                    type="text"
                    value={editForm.destination_url}
                    onChange={(e) => setEditForm({...editForm, destination_url: e.target.value})}
                    className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-white text-sm"
                    placeholder="https://example.com"
                  />
                </div>
                <div>
                  <label className="text-xs text-gray-400 mb-1 block">Başlık (isteğe bağlı)</label>
                  <input
                    type="text"
                    value={editForm.title}
                    onChange={(e) => setEditForm({...editForm, title: e.target.value})}
                    className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-white text-sm"
                    placeholder="Link başlığı"
                  />
                </div>
                <button onClick={handleUpdateLink} className="w-full btn-purple py-2 text-sm">Güncelle</button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Stats Modal */}
      <AnimatePresence>
        {showStatsModal && statsModalLink && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-2">
            <motion.div initial={{ scale: 0.9 }} animate={{ scale: 1 }} exit={{ scale: 0.9 }} className="bg-[#0a0a0a] border border-blue-500/30 rounded-xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
              <div className="sticky top-0 bg-[#0a0a0a] border-b border-blue-500/30 p-4 flex justify-between items-center">
                <div>
                  <h3 className="text-lg font-bold text-white">Link İstatistikleri</h3>
                  <p className="text-blue-500 font-mono text-sm">/{statsModalLink.short_code}</p>
                </div>
                <button onClick={() => { setShowStatsModal(false); setStatsModalData(null); }} className="text-gray-400 hover:text-white"><X className="w-5 h-5" /></button>
              </div>
              <div className="p-4 space-y-4">
                {!statsModalData ? (
                  <div className="text-center py-8">
                    <div className="animate-spin rounded-full h-8 w-8 border-2 border-blue-500 border-t-transparent mx-auto mb-2"></div>
                    <p className="text-gray-400 text-sm">Yükleniyor...</p>
                  </div>
                ) : (
                  <>
                    {/* Özet Kartlar */}
                    <div className="grid grid-cols-2 gap-3">
                      <div className="bg-blue-500/10 border border-blue-500/30 rounded-xl p-3 text-center">
                        <div className="text-3xl font-bold text-blue-400">{statsModalData.total_clicks || 0}</div>
                        <div className="text-gray-400 text-xs mt-1">Toplam Tıklama</div>
                      </div>
                      <div className="bg-green-500/10 border border-green-500/30 rounded-xl p-3 text-center">
                        <div className="text-3xl font-bold text-green-400">{statsModalData.total_whatsapp || 0}</div>
                        <div className="text-gray-400 text-xs mt-1">WhatsApp</div>
                      </div>
                      <div className="bg-purple-500/10 border border-purple-500/30 rounded-xl p-3 text-center">
                        <div className="text-3xl font-bold text-purple-400">{statsModalData.unique_visitors || 0}</div>
                        <div className="text-gray-400 text-xs mt-1">Benzersiz</div>
                      </div>
                      <div className="bg-purple-500/10 border border-purple-500/30 rounded-xl p-3 text-center">
                        <div className="text-3xl font-bold text-purple-400">{Object.keys(statsModalData.countries || {}).length}</div>
                        <div className="text-gray-400 text-xs mt-1">Ülke</div>
                      </div>
                    </div>

                    {/* Ülkeler */}
                    {Object.keys(statsModalData.countries || {}).length > 0 && (
                      <div className="bg-white/5 rounded-xl p-3">
                        <h4 className="text-sm font-bold text-white mb-2 flex items-center gap-2"><Globe className="w-4 h-4" /> Ülkeler</h4>
                        <div className="space-y-1 max-h-32 overflow-y-auto">
                          {Object.entries(statsModalData.countries || {}).slice(0, 5).map(([country, count]) => {
                            const code = getCountryCode(country);
                            return (
                              <div key={country} className="flex justify-between items-center text-xs p-1.5 bg-white/5 rounded">
                                <span className="text-white flex items-center gap-2">
                                  {code && <img src={`https://flagcdn.com/16x12/${code.toLowerCase()}.png`} alt={country} className="w-4 h-3" />}
                                  {country}
                                </span>
                                <span className="text-purple-500 font-bold">{count}</span>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    )}

                    {/* Tarayıcılar */}
                    {Object.keys(statsModalData.browsers || {}).length > 0 && (
                      <div className="bg-white/5 rounded-xl p-3">
                        <h4 className="text-sm font-bold text-white mb-2 flex items-center gap-2"><Chrome className="w-4 h-4" /> Tarayıcılar</h4>
                        <div className="flex flex-wrap gap-2">
                          {Object.entries(statsModalData.browsers || {}).slice(0, 5).map(([browser, count]) => (
                            <span key={browser} className="text-xs bg-white/10 px-2 py-1 rounded text-gray-300">
                              {browser}: <span className="text-purple-500 font-bold">{count}</span>
                            </span>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Cihazlar */}
                    {Object.keys(statsModalData.device_types || {}).length > 0 && (
                      <div className="bg-white/5 rounded-xl p-3">
                        <h4 className="text-sm font-bold text-white mb-2 flex items-center gap-2"><Smartphone className="w-4 h-4" /> Cihaz Tipleri</h4>
                        <div className="flex flex-wrap gap-2">
                          {Object.entries(statsModalData.device_types || {}).map(([device, count]) => (
                            <span key={device} className="text-xs bg-white/10 px-2 py-1 rounded text-gray-300">
                              {device}: <span className="text-purple-500 font-bold">{count}</span>
                            </span>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Hedef URL */}
                    <div className="bg-white/5 rounded-xl p-3">
                      <h4 className="text-sm font-bold text-white mb-2">Hedef URL</h4>
                      <p className="text-xs text-gray-400 break-all">{statsModalLink.destination_url}</p>
                    </div>
                  </>
                )}
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* WhatsApp Modal */}
      <AnimatePresence>
        {showWhatsAppModal && analytics && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-2">
            <motion.div initial={{ scale: 0.9 }} animate={{ scale: 1 }} exit={{ scale: 0.9 }} className="bg-[#0a0a0a] border border-green-500/30 rounded-xl w-full max-w-md max-h-[90vh] overflow-y-auto">
              <div className="sticky top-0 bg-[#0a0a0a] border-b border-green-500/30 p-4 flex justify-between items-center">
                <div className="flex items-center gap-3">
                  <img src="/whatsapp-logo.png" alt="WhatsApp" className="w-10 h-10 rounded" />
                  <div>
                    <h3 className="text-lg font-bold text-white">WhatsApp Yönlendirmeleri</h3>
                    <p className="text-gray-400 text-xs">Başarılı yönlendirme istatistikleri</p>
                  </div>
                </div>
                <button onClick={() => setShowWhatsAppModal(false)} className="text-gray-400 hover:text-white"><X className="w-5 h-5" /></button>
              </div>
              <div className="p-4 space-y-4">
                {/* Bugün */}
                <div className="bg-green-500/10 border border-green-500/30 rounded-xl p-4 text-center">
                  <div className="text-5xl font-bold text-green-400 mb-2">{analytics.today_whatsapp || 0}</div>
                  <div className="text-gray-400 text-sm">Bugün (TR 00:00-00:00)</div>
                </div>
                
                {/* Toplam */}
                <div className="bg-white/5 border border-white/10 rounded-xl p-4 text-center">
                  <div className="text-3xl font-bold text-white mb-2">{analytics.total_whatsapp || 0}</div>
                  <div className="text-gray-400 text-sm">Son 14 Gün Toplam</div>
                </div>
                
                {/* Bilgi */}
                <div className="bg-white/5 rounded-lg p-3 text-xs text-gray-400">
                  <p>📊 WhatsApp yönlendirmeleri otomatik olarak sayılır.</p>
                  <p className="mt-1">🕐 Her gün TR saati 00:00'da günlük sayaç sıfırlanır.</p>
                  <p className="mt-1">📅 14 günden eski veriler otomatik temizlenir.</p>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default UserDashboard;
